import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Badge,
  EmptyState,
  CircularLoader,
  Banner,
  GraphIcon,
  CheckIcon,
  ErrorIcon,
  useDocumentTitle,
} from '@forge/common';
import { useJobRuns } from '@/hooks/useJobRuns';
import { usePartners } from '@/hooks/usePartners';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';

const ActivityPage = () => {
  useDocumentTitle('Activity — Partner Exchange — OneRing Portal');
  const navigate = useNavigate();

  const { partners } = usePartners();
  const { runs, isLoading, error, isUsingMockData } = useJobRuns();

  const partnerNameById = useMemo(
    () => new Map(partners.map((p) => [p.id, p.displayName])),
    [partners],
  );

  const sortedRuns = useMemo(
    () => [...runs].sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()),
    [runs],
  );

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">Activity</h2>
      <p className="text-sm text-color-tertiary mb-4">
        Recent partner transfer activity across the platform, most recent first.
      </p>

      {isUsingMockData && (
        <div className="mb-4">
          <MockDataBanner featureName="Activity" />
        </div>
      )}

      {error ? (
        <Banner
          variant="inline"
          type="error"
          title="Failed to load activity"
          description={error}
          canClose={false}
        />
      ) : (
        <Card>
          <CardHeader title="Recent Activity" />
          <CardContent>
            {isLoading ? (
              <div className="flex min-h-[240px] items-center justify-center">
                <CircularLoader size="large" />
              </div>
            ) : sortedRuns.length === 0 ? (
              <EmptyState icon={<GraphIcon />} description="No activity yet" />
            ) : (
              <div className="flex flex-col">
                {sortedRuns.map((run) => (
                  <div
                    key={run.id}
                    className="flex cursor-pointer items-start gap-3 border-b border-gray-100 py-3 last:border-b-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
                    onClick={() => run.partnerId && navigate(`/partners/${run.partnerId}`)}
                  >
                    <span className="mt-0.5">
                      {run.status === 'failed' ? <ErrorIcon /> : <CheckIcon />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">
                        <span className="font-mono font-medium">{run.jobName}</span>{' '}
                        <span className="text-color-tertiary">
                          {run.direction === 'outbound' ? 'sent to' : 'pulled from'}
                        </span>{' '}
                        <span className="font-medium">
                          {run.partnerId ? partnerNameById.get(run.partnerId) ?? run.partnerId : 'unknown partner'}
                        </span>
                      </p>
                      {run.error && (
                        <p className="mt-1 text-xs text-color-error">{run.error}</p>
                      )}
                      <p className="mt-1 text-xs text-color-tertiary">
                        {new Date(run.startedAt).toLocaleString()} · {run.filesProcessed} file
                        {run.filesProcessed !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <Badge color={run.status === 'failed' ? 'red' : run.status === 'success' ? 'green' : 'blue'}>
                      {run.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ActivityPage;
